import * as Yup from 'yup';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useFieldArray } from 'react-hook-form';

import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import {
  Card,
  Grid,
  Badge,
  Stack,
  Select,
  Switch,
  Divider,
  MenuItem,
  useTheme,
  CardHeader,
  IconButton,
  InputLabel,
  Typography,
  CardContent,
  FormControl,
  useMediaQuery,
  FormHelperText,
  FormControlLabel,
  SelectChangeEvent,
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { getUpdateFieldConfigErrorMessage } from 'src/services/custom-fields/custom-field.utils';
import {
  useGetConfigByEntityTypeQuery,
  useUpdateCustomFieldConfigMutation,
} from 'src/services/custom-fields/custom-field.service';
import {
  CustomFieldType,
  ICustomFieldConfig,
  CustomFieldEntityType,
  UpdateCustomFieldConfigDto,
} from 'src/services/custom-fields/custom-field.types';

import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import FeatureLockedListView from 'src/components/billing/feature-locked-list-view';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';
import { LoadingCardItem } from 'src/sections/admin/billing/payment/payment-card-item';

import { FeatureType } from 'src/types/subscription.types';

const MAX_FIELDS_PER_ENTITY_TYPE = 5;

export default function CustomFieldSettingsCard() {

  const { t } = useTranslate();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const authContext = useAuthContext();

  const { canAccess } = useFeatures();

  const [selectedEntityType, setSelectedEntityType] = useState<CustomFieldEntityType>(
    CustomFieldEntityType.CONTACT
  );

  const {
    data: configData,
    isFetching: isLoadingConfig,
    refetch,
  } = useGetConfigByEntityTypeQuery({
    entityType: selectedEntityType
  }, {
    skip: !authContext?.organization?.id || !selectedEntityType
  });

  const [updateConfig, { isLoading: isUpdating }] = useUpdateCustomFieldConfigMutation();

  const CustomFieldSettingsSchema = Yup.object().shape({
    configs: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().nullable(),
          label: Yup.string().required(t('validation.required')),
          field_type: Yup.string().required(t('validation.required')),
          options: Yup.array().when('field_type', {
            is: CustomFieldType.SELECT,
            then: (schema) =>
              schema
                .of(Yup.string().required(t('validation.required')))
                .min(1, t('validation.min_one_option_required')),
            otherwise: (schema) => schema.nullable(),
          }),
          is_required: Yup.boolean().nullable(),
        })
      )
      .max(5, t('validation.max_custom_fields', { max: 5 }))
      .required(t('validation.required')),
  });

  const methods = useForm({
    resolver: yupResolver(CustomFieldSettingsSchema),
    defaultValues: {
      configs: [],
    },
  });

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'configs',
  });

  const watchFieldArray = watch('configs');
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watchFieldArray[index],
  }));

  useEffect(() => {
    if (configData) {
      const sortedData = [...configData].sort((a, b) => moment(a.created_at).diff(moment(b.created_at)));

      setValue(
        'configs',
        sortedData.map((config: ICustomFieldConfig) => ({
          id: config.id,
          label: config.label,
          field_type: config.field_type,
          options: config.options || [],
          is_required: config.is_required || false,
        }))
      );
    }
  }, [configData, setValue]);

  const handleEntityTypeChange = (event: SelectChangeEvent<CustomFieldEntityType>) => {
    setSelectedEntityType(event.target.value as CustomFieldEntityType);
  };

  const handleAddOption = (fieldIndex: number) => {
    const currentConfigs = [...watchFieldArray];

    if (!currentConfigs[fieldIndex].options) {
      currentConfigs[fieldIndex].options = [];
    }

    currentConfigs[fieldIndex].options?.push('');
    setValue('configs', currentConfigs);

  };

  const handleRemoveOption = (fieldIndex: number, optionIndex: number) => {
    const currentConfigs = [...watchFieldArray];
    if (currentConfigs[fieldIndex] && currentConfigs[fieldIndex].options) {
      currentConfigs[fieldIndex].options?.splice(optionIndex, 1);
      setValue('configs', currentConfigs);
    }
  };

  const handleOptionChange = (fieldIndex: number, optionIndex: number, value: string) => {
    setValue(`configs.${fieldIndex}.options.${optionIndex}`, value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      const updateData: UpdateCustomFieldConfigDto = {
        configs: data.configs.map((config: any) => ({
          id: config.id || undefined,
          field_type: config.field_type as CustomFieldType,
          label: config.label.trim(),
          options: config.field_type === CustomFieldType.SELECT ? config.options : undefined,
          is_required: config.is_required,
        })),
      };

      await updateConfig({
        entityType: selectedEntityType,
        data: updateData,
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.custom_field_settings.api.update.success'), {
        variant: 'success',
      });

      refetch();
    } catch (e) {
      enqueueSnackbar(getUpdateFieldConfigErrorMessage(e), {
        variant: 'error',
      });
    }
  });

  const canEditSettings = useMemo(() => {
    if (!authContext?.account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(authContext.account.org_role);
  }, [authContext]);

  const handleAddField = () => {
    append({
      id: null,
      label: '',
      field_type: CustomFieldType.TEXT,
      options: [],
      is_required: false,
    });
  };

  return (
    <FormProvider methods={methods}>
      <Card sx={{ width: '100%', boxShadow: 0 }}>
        <CardHeader
          avatar={
            <Iconify
              icon="mdi:form-textbox"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6">{t('admin-organization.settings.custom_field_settings.title')}</Typography>
          }
        />

        <CardContent>
          <Stack spacing={2} sx={{ px: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="entity-type-select-label">
                    {t('admin-organization.settings.custom_field_settings.entity_type')}
                  </InputLabel>
                  <Select
                    labelId="entity-type-select-label"
                    value={selectedEntityType}
                    label={t('admin-organization.settings.custom_field_settings.entity_type')}
                    onChange={handleEntityTypeChange}
                  >
                    {Object.values(CustomFieldEntityType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(`admin-organization.settings.custom_field_settings.entity_types.${type}`)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {t('admin-organization.settings.custom_field_settings.entity_type_helper')}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              {
                !isLoadingConfig ? (
                  <>
                    {
                      canAccess(FeatureType.CUSTOM_FIELDS_ENABLED) ?
                        <Grid item xs={12}>
                          <Stack direction="column" my={2} mb={4}>
                            <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
                              <Stack direction="row" gap={2} alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {t('admin-organization.settings.custom_field_settings.custom_fields')}
                                </Typography>

                                {fields?.length > 0 && <Badge badgeContent={fields?.length} color="primary" />}
                              </Stack>

                              <IconButton onClick={handleAddField} disabled={!canEditSettings || fields?.length >= MAX_FIELDS_PER_ENTITY_TYPE}>
                                <Iconify icon="formkit:add" />
                              </IconButton>
                            </Stack>

                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {t('admin-organization.settings.custom_field_settings.custom_fields_helper')}
                            </Typography>
                          </Stack>

                          {controlledFields?.length > 0 && (
                            <Stack direction="column" gap={3}>
                              {controlledFields?.map((field, index) => (
                                <Card key={`${index}_${fields.length}`} sx={{ p: 2, boxShadow: 1 }}>
                                  <Stack direction="column" gap={2}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                      <Typography variant="subtitle2">
                                        {field.label || t('admin-organization.settings.custom_field_settings.new_field')}
                                      </Typography>
                                      <IconButton
                                        onClick={() => remove(index)}
                                        color="error"
                                        disabled={!canEditSettings}
                                      >
                                        <Iconify icon="bi:trash" />
                                      </IconButton>
                                    </Stack>

                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={6}>
                                        <RHFTextField
                                          name={`configs.${index}.label`}
                                          label={t('admin-organization.settings.custom_field_settings.field_label')}
                                          disabled={!canEditSettings}
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <RHFSelect
                                          name={`configs.${index}.field_type`}
                                          label={t('admin-organization.settings.custom_field_settings.field_type')}
                                          disabled={!canEditSettings || field.id !== null}
                                        >
                                          {Object.values(CustomFieldType).map((type) => (
                                            <MenuItem key={type} value={type}>
                                              {t(`admin-organization.settings.custom_field_settings.field_types.${type}`)}
                                            </MenuItem>
                                          ))}
                                        </RHFSelect>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <FormControlLabel
                                          control={
                                            <Switch

                                              checked={field.is_required || false}
                                              onChange={(e) => {
                                                const currentConfigs = [...watchFieldArray];
                                                currentConfigs[index].is_required = e.target.checked;
                                                setValue('configs', currentConfigs);
                                              }}
                                              disabled={!canEditSettings || field.id !== null}
                                            />
                                          }
                                          label={t('admin-organization.settings.custom_field_settings.is_required')}
                                        />
                                      </Grid>
                                    </Grid>

                                    {field.field_type === CustomFieldType.SELECT && (
                                      <Box>
                                        <Stack
                                          direction="row"
                                          justifyContent="space-between"
                                          alignItems="center"
                                          mb={1}
                                        >
                                          <Typography variant="body2">
                                            {t('admin-organization.settings.custom_field_settings.options')}
                                          </Typography>
                                          <IconButton
                                            onClick={() => handleAddOption(index)}
                                            size="small"
                                            disabled={!canEditSettings}
                                          >
                                            <Iconify icon="formkit:add" />
                                          </IconButton>
                                        </Stack>

                                        {field.options && field.options.length > 0 ? (
                                          <Stack spacing={1}>
                                            {field.options.map((option, optionIndex) => (
                                              <Stack
                                                key={optionIndex}
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                              >
                                                <RHFTextField
                                                  name={`configs.${index}.options.${optionIndex}`}
                                                  label={t(
                                                    'admin-organization.settings.custom_field_settings.option_value'
                                                  )}
                                                  value={option}
                                                  onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                                                  disabled={!canEditSettings}
                                                  fullWidth
                                                />
                                                <IconButton
                                                  onClick={() => handleRemoveOption(index, optionIndex)}
                                                  color="error"
                                                  disabled={!canEditSettings}
                                                >
                                                  <Iconify icon="bi:trash" />
                                                </IconButton>
                                              </Stack>
                                            ))}
                                          </Stack>
                                        ) : (
                                          <Typography variant="body2" color="text.secondary">
                                            {t('admin-organization.settings.custom_field_settings.no_options')}
                                          </Typography>
                                        )}

                                        {errors?.configs?.[index]?.options && (
                                          <FormHelperText error>
                                            {errors.configs[index]?.options?.message}
                                          </FormHelperText>
                                        )}
                                      </Box>
                                    )}
                                  </Stack>
                                </Card>
                              ))}
                            </Stack>
                          )}

                          {controlledFields?.length === 0 && (
                            <Stack>
                              <EmptyContent
                                title={t('admin-organization.settings.custom_field_settings.no_fields_title')}
                                description={t('admin-organization.settings.custom_field_settings.no_fields_description')}
                              />
                            </Stack>
                          )}
                        </Grid>
                        :
                        (
                          <FeatureLockedListView text={t('billing.subscription_required')} />
                        )
                    }
                  </>

                ) : (
                  <Grid item xs={12}>
                    <Stack spacing={2}>
                      <LoadingCardItem />
                      <LoadingCardItem />
                      <LoadingCardItem />
                    </Stack>
                  </Grid>
                )
              }

            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <LoadingButton
                type="submit"
                onClick={onSubmit}
                variant="contained"
                loading={isSubmitting || isUpdating}
                disabled={!isValid || !canEditSettings || isLoadingConfig || controlledFields.length === 0 || !canAccess(FeatureType.CUSTOM_FIELDS_ENABLED)}
              >
                {t('common.update')}
              </LoadingButton>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </FormProvider>
  );
}